import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro, euroDezimal } from "@/utils/preisAnalyse";

/**
 * Der hochzeitsspezifische Teil der Seite.
 *
 * Bewusst nichts davon steht so auf der Startseite: Dort geht es darum, was
 * eine Fotobox kostet und welche Bauformen es gibt. Hier geht es um die
 * Fragen, die nur bei einer Hochzeit auftauchen — wie viele Ausdrucke ein
 * Gästebuch zusätzlich frisst, wann im Ablauf die Box wirklich genutzt wird
 * und wer sie aufbaut, wenn am Hochzeitstag niemand Zeit hat.
 *
 * Ohne diesen Unterschied wäre die Seite eine zweite Startseite, und Google
 * würde weiter die Startseite ausspielen — genau das ist bisher passiert.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

function H3({ children }) {
  return (
    <h3 className="mt-8 text-lg font-semibold text-slate-900">{children}</h3>
  );
}

export default function HochzeitInhalt({ analyse, stand }) {
  if (!analyse) return null;

  return (
    <>
      <Abschnitt
        id="kosten"
        titel="Was kostet eine Fotobox für die Hochzeit?"
        untertitel={`Gerechnet für ${analyse.gaeste} Gäste — mit Ausdrucken, die den ganzen Abend reichen.`}
      >
        <p>
          Für eine Hochzeit mit{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.gaeste} Gästen
          </strong>{" "}
          solltet ihr mit rund{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.bedarf} Ausdrucken
          </strong>{" "}
          rechnen. Von den {analyse.anzahlPaketeGesamt} Paketen im Vergleich
          schaffen das{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.anzahlPakete}
          </strong>{" "}
          — entweder mit genug Papier oder mit einer Druck-Flatrate. Sie
          beginnen bei{" "}
          <strong className="font-semibold text-slate-900">
            {euro(analyse.min)} €
          </strong>
          , die Hälfte liegt unter{" "}
          <strong className="font-semibold text-slate-900">
            {euro(analyse.median)} €
          </strong>
          .
        </p>
        <p className="mt-4">
          Umgerechnet sind das rund{" "}
          <strong className="font-semibold text-slate-900">
            {euroDezimal(analyse.proGast)} € pro Gast
          </strong>{" "}
          — in den meisten Hochzeitsbudgets weniger als das Gastgeschenk und
          deutlich weniger als eine Stunde Fotograf. Anders als beim Fotografen
          bekommt jeder Gast am selben Abend etwas in die Hand.
        </p>
        <p className="mt-4">
          Günstiger wird es nur ohne Drucker: Rein digitale Pakete gibt es ab{" "}
          <Link
            href="/fotobox-mieten-unter-100-eur"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            unter 100 €
          </Link>
          . Dann fehlt aber genau das, was auf einer Hochzeit den Unterschied
          macht — der Streifen, den die Gäste mitnehmen und ins Gästebuch
          kleben.
        </p>
      </Abschnitt>

      <Abschnitt
        id="bilder"
        titel="Wie viele Bilder braucht ihr wirklich?"
        untertitel="Die Rechnung, die die meisten unterschätzen."
      >
        <p>
          Die Faustregel lautet zwei bis drei Ausdrucke pro Gast. Das klingt
          nach viel, ist aber schnell erreicht: Die meisten Gäste gehen nicht
          einmal in die Box, sondern drei- bis viermal — einmal mit dem Partner,
          einmal mit dem Tisch, einmal spät abends mit allen, die noch da sind.
          Kinder öfter.
        </p>

        <H3>Der Punkt, den fast alle vergessen</H3>
        <p className="mt-3">
          Wenn ihr ein{" "}
          <strong className="font-semibold text-slate-900">
            Fotobox-Gästebuch
          </strong>{" "}
          plant, druckt die Box jedes Bild <em>zweimal</em>: einmal für den
          Gast, einmal zum Einkleben. Damit verdoppelt sich der Bedarf — aus{" "}
          {analyse.bedarf} werden {analyse.bedarf * 2} Ausdrucke. Genau hier
          geht bei vielen Hochzeiten mitten in der Nacht das Papier aus.
        </p>
        <p className="mt-4">
          Zwei Wege aus dem Problem: ein Paket mit ausreichend großer Rolle —
          oder eine{" "}
          <Link
            href="/fotobox-mieten-mit-sofortdruck"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Druck-Flatrate
          </Link>
          , bei der die Menge egal ist. Die bieten{" "}
          <strong className="font-semibold text-slate-900">
            {analyse.mitFlatrate} der {analyse.anzahlBoxen} Fotoboxen
          </strong>{" "}
          im Vergleich an.
        </p>

        <H3>Was ihr fürs Gästebuch braucht</H3>
        <ul className="mt-3 list-disc space-y-2 pl-5 marker:text-slate-400">
          <li>Ein Buch mit festen, dunklen Seiten — darauf kommen die Streifen gut.</li>
          <li>Fotoecken oder doppelseitige Klebepunkte, keine Klebestifte.</li>
          <li>Zwei bis drei Stifte, die auf Fotopapier schreiben.</li>
          <li>
            Einen kleinen Tisch direkt neben der Box. Steht das Buch woanders,
            landen die Bilder in Handtaschen.
          </li>
        </ul>
      </Abschnitt>

      <Abschnitt
        id="ablauf"
        titel="Wann im Ablauf der Hochzeit?"
        untertitel="Wann die Box tatsächlich benutzt wird — und wann sie nur herumsteht."
      >
        <p>
          Die Box sollte stehen, bevor die Gäste kommen, und bis zum Schluss
          bleiben. Sie kostet nicht mehr, wenn sie länger da ist — der Mietpreis
          gilt fast überall für den ganzen Tag oder das Wochenende.
        </p>
        <dl className="mt-6 space-y-4">
          <div className="border-l-2 border-slate-200 pl-5">
            <dt className="font-semibold text-slate-900">Sektempfang</dt>
            <dd className="mt-1 text-sm leading-6 text-slate-600">
              Wenig Betrieb, aber die schönsten Bilder: Alle sind frisch
              zurechtgemacht, das Licht ist gut, und die Großeltern gehen jetzt
              rein — später nicht mehr.
            </dd>
          </div>
          <div className="border-l-2 border-slate-200 pl-5">
            <dt className="font-semibold text-slate-900">Nach dem Essen</dt>
            <dd className="mt-1 text-sm leading-6 text-slate-600">
              Die Hauptzeit. Zwischen Dessert und Eröffnungstanz entsteht die
              Hälfte aller Bilder eines Abends.
            </dd>
          </div>
          <div className="border-l-2 border-slate-200 pl-5">
            <dt className="font-semibold text-slate-900">Ab Mitternacht</dt>
            <dd className="mt-1 text-sm leading-6 text-slate-600">
              Die lustigsten Aufnahmen — und der Moment, in dem das Papier
              ausgeht, wenn zu knapp kalkuliert wurde.
            </dd>
          </div>
        </dl>
        <p className="mt-6">
          Ein Hinweis zum Standort: Nicht direkt neben die Boxen des DJs. Dort
          ist es zu laut zum Absprechen, und der Bereich vor der Box wird zur
          Tanzfläche. Zwei mal zwei Meter freie Fläche und eine Steckdose in
          Reichweite genügen.
        </p>
      </Abschnitt>

      <Abschnitt
        id="aufbau"
        titel="Wer baut die Fotobox auf?"
        untertitel="Am Hochzeitstag hat niemand Zeit — deshalb wird das vorher entschieden."
      >
        <p>
          Das ist die Frage, an der die Wahl zwischen Versand und Anbieter vor
          Ort hängt. Am Hochzeitstag selbst hat weder das Brautpaar noch die
          Trauzeugin eine freie Viertelstunde.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200/70">
            <h3 className="text-base font-semibold text-slate-900">
              Versand-Fotobox
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Kommt ein bis fünf Tage vorher per Paket. Der Aufbau dauert 10 bis
              15 Minuten und lässt sich am Vortag erledigen — üblicherweise von
              den Trauzeugen, wenn die Location ohnehin dekoriert wird. Nach der
              Feier zurück ins Paket, Retourenschein liegt bei.
            </p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200/70">
            <h3 className="text-base font-semibold text-slate-900">
              Anbieter vor Ort
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {analyse.bauenAuf} Anbieter im Vergleich liefern, bauen auf und
              holen wieder ab. Ihr habt damit nichts zu tun — dafür kostet es
              mehr, und der Anbieter muss in eure Region liefern.
            </p>
          </div>
        </div>
        <p className="mt-6">
          Klärt vorher zwei Dinge mit der Location: ob am gewünschten Platz eine
          Steckdose ist und ob jemand die Box am Folgetag herausgeben kann. Beides
          klingt banal und wird regelmäßig übersehen.
        </p>
      </Abschnitt>

      <Abschnitt
        id="ausstattung"
        titel="Worauf es bei einer Hochzeits-Fotobox ankommt"
        untertitel="Vier Dinge, die abends den Unterschied machen."
      >
        <dl className="space-y-5">
          <div className="border-l-2 border-slate-200 pl-5">
            <dt className="font-semibold text-slate-900">
              Blitz statt Dauerlicht
            </dt>
            <dd className="mt-1 text-sm leading-6 text-slate-600">
              Ab dem Eröffnungstanz ist es dunkel, und getanzt wird auch vor der
              Box. Ein Studio-Blitz friert Bewegung ein; LED-Dauerlicht liefert
              dann verwackelte Bilder.
            </dd>
          </div>
          <div className="border-l-2 border-slate-200 pl-5">
            <dt className="font-semibold text-slate-900">
              Eigenes Drucklayout
            </dt>
            <dd className="mt-1 text-sm leading-6 text-slate-600">
              Namen und Datum auf jedem Streifen — {analyse.mitEigenemLayout}{" "}
              Fotoboxen erlauben ein eigenes Layout, {analyse.mitLayout} bieten
              zumindest eine Auswahl an Vorlagen.
            </dd>
          </div>
          <div className="border-l-2 border-slate-200 pl-5">
            <dt className="font-semibold text-slate-900">Online-Galerie</dt>
            <dd className="mt-1 text-sm leading-6 text-slate-600">
              Die Ausdrucke bleiben bei den Gästen. Damit ihr die Bilder
              trotzdem habt, liefern {analyse.mitGalerie} Fotoboxen eine
              Online-Galerie — meist am Tag danach.
            </dd>
          </div>
          <div className="border-l-2 border-slate-200 pl-5">
            <dt className="font-semibold text-slate-900">Requisiten in Maßen</dt>
            <dd className="mt-1 text-sm leading-6 text-slate-600">
              Ein paar Hüte und Brillen lockern auf. Eine ganze Kiste sorgt
              dafür, dass auf jedem Bild eine Plastikbrille sitzt — auch auf
              denen, die ihr aufheben wollt.
            </dd>
          </div>
        </dl>
        <p className="mt-6 text-xs text-slate-500">
          Zahlen aus den {analyse.anzahlBoxen} Fotoboxen im Vergleich, Stand{" "}
          {stand}.
        </p>
      </Abschnitt>
    </>
  );
}
